import React, { useEffect, useRef, useState } from 'react';
import {
  buildRemoveAlbumConfirmMessage,
  getPerAlbumActionErrorHint,
  logConnectorVersionOnce,
  PER_ALBUM_UPDATE_HINT,
} from '../../utils/localAlbumActions';

/**
 * Visible ⋯ overflow for per-album Re-read / Remove on album tiles.
 */
export default function AlbumTileOverflowMenu({
  album,
  onReread,
  onRemove,
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(null);
  const [hint, setHint] = useState(null);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const run = async (action, fn) => {
    if (busy || disabled) return;
    logConnectorVersionOnce();
    setHint(null);
    setBusy(action);
    try {
      await fn?.();
      setOpen(false);
    } catch (e) {
      setHint(getPerAlbumActionErrorHint(e) || e?.message || 'Action failed');
    } finally {
      setBusy(null);
    }
  };

  const handleReread = (e) => {
    e.stopPropagation();
    e.preventDefault();
    void run('reread', onReread);
  };

  const handleRemove = (e) => {
    e.stopPropagation();
    e.preventDefault();
    const title = album?.title || album?.album || 'this album';
    if (!confirm(buildRemoveAlbumConfirmMessage(title))) return;
    void run('remove', onRemove);
  };

  const toggleOpen = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (disabled) return;
    setHint(null);
    setOpen((v) => !v);
  };

  const itemStyle = {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '8px 12px',
    fontSize: 12,
    fontWeight: 600,
    textAlign: 'left',
    background: 'transparent',
    border: 'none',
    cursor: busy ? 'wait' : 'pointer',
    color: 'var(--text-primary)',
  };

  return (
    <div ref={rootRef} className="relative" style={{ zIndex: 4 }}>
      <button
        type="button"
        className={`flex items-center justify-center transition-opacity duration-150 ${disabled ? 'opacity-40' : 'opacity-100 pointer-fine:opacity-0 pointer-fine:group-hover:opacity-100 pointer-coarse:opacity-100'}`}
        style={{
          width: 28,
          height: 28,
          borderRadius: 14,
          background: 'rgba(0,0,0,0.65)',
          backdropFilter: 'blur(4px)',
          border: 'none',
          cursor: disabled ? 'default' : 'pointer',
          padding: 0,
          boxShadow: '0 1px 6px rgba(0,0,0,0.4)',
          color: '#fff',
          fontSize: 16,
          lineHeight: 1,
          fontWeight: 700,
        }}
        title="Album actions"
        aria-label="Album actions"
        disabled={disabled}
        onClick={toggleOpen}
      >
        ⋯
      </button>
      {open && (
        <div
          className="absolute top-full right-0 mt-1 rounded-lg overflow-hidden min-w-[180px] py-1"
          style={{
            background: 'var(--bg-primary)',
            border: '1px solid var(--border)',
            boxShadow: '0 12px 32px rgba(0,0,0,0.32)',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <button type="button" style={itemStyle} disabled={!!busy} onClick={handleReread}>
            {busy === 'reread' ? 'Re-reading…' : 'Re-read'}
          </button>
          <button
            type="button"
            style={{ ...itemStyle, color: '#ef4444' }}
            disabled={!!busy}
            onClick={handleRemove}
          >
            {busy === 'remove' ? 'Removing…' : 'Remove from library'}
          </button>
          {hint && (
            <div style={{ padding: '6px 12px 8px', fontSize: 11, color: '#fbbf24', lineHeight: 1.35 }}>
              {hint === PER_ALBUM_UPDATE_HINT ? PER_ALBUM_UPDATE_HINT : hint}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
